import { useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Info, Loader2, Trash2, X } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { usePatientDialog } from '../patients/use-patient-dialog'
import type { SpecialRule } from './assistant-settings-contracts'
import panel from './SettingsSection.module.css'
import styles from './AiRulesSettings.module.css'

const MAX_RULE_LENGTH = 1000

/** What saving does to this rule, in the owner's lifecycle terms. */
function lifecycleNote(rule: SpecialRule | undefined) {
  if (!rule) {
    return 'New rules start as drafts. Turn a rule on to start using it.'
  }
  if (rule.status === 'ACTIVE') {
    return 'This rule is in use. Saved changes are used right away.'
  }
  if (rule.status === 'INACTIVE') {
    return 'This rule is off. Saving changes makes it a draft; turn it on to use it again.'
  }
  return 'This draft isn’t used yet. Turn it on to start using it.'
}

/**
 * Add or edit one rule. Saving and deleting go straight to the server (rules
 * have no page draft); the dialog stays open with the reason if either fails.
 */
export function RuleDialog({
  title,
  eyebrow,
  rule,
  saving,
  error,
  onSubmit,
  onDelete,
  onClose,
}: {
  title: string
  eyebrow: string
  rule?: SpecialRule
  saving: boolean
  error: string | null
  onSubmit: (content: string) => void
  onDelete?: () => void
  onClose: () => void
}) {
  const dialog = useRef<HTMLElement>(null)
  const safeChoice = useRef<HTMLButtonElement>(null)
  const titleId = useId()
  const fieldId = useId()
  const [content, setContent] = useState(rule?.content ?? '')
  const [mode, setMode] = useState<'edit' | 'discard' | 'delete'>('edit')
  const trimmed = content.trim()
  const changed = trimmed !== (rule?.content ?? '').trim()
  const tooLong = trimmed.length > MAX_RULE_LENGTH

  function ask(next: 'discard' | 'delete') {
    setMode(next)
    window.setTimeout(() => safeChoice.current?.focus(), 0)
  }

  function requestClose() {
    if (saving) return
    if (changed && trimmed) ask('discard')
    else onClose()
  }

  usePatientDialog(dialog, {
    onClose: () => (mode === 'edit' ? requestClose() : setMode('edit')),
    locked: saving,
  })

  return createPortal(
    <div className={panel.backdrop} role="presentation">
      <section
        ref={dialog}
        className={`${panel.dialog} ${styles.ruleDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className={panel.dialogHeader}>
          <div>
            <span className={panel.eyebrow}>{eyebrow}</span>
            <h2 id={titleId}>{title}</h2>
          </div>
          <button
            type="button"
            className={panel.closeButton}
            aria-label="Close"
            disabled={saving}
            onClick={requestClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {mode === 'delete' && rule ? (
          <div className={panel.dialogBody}>
            <div
              className={styles.confirmPanel}
              role="group"
              aria-labelledby={`${titleId}-delete`}
            >
              <span className={styles.confirmIcon} aria-hidden="true">
                <Trash2 size={18} />
              </span>
              <div>
                <h3 id={`${titleId}-delete`}>Delete this rule?</h3>
                <blockquote className={styles.ruleQuote}>
                  {rule.content}
                </blockquote>
                <ul>
                  {rule.status === 'ACTIVE' ? (
                    <li>Ava stops using it right away.</li>
                  ) : null}
                  <li>It’s removed from this list and can’t be restored.</li>
                  <li>Other rules aren’t affected.</li>
                </ul>
                {error ? (
                  <p className={panel.fieldError} role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    {error}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className={panel.dialogBody}>
            <div className={panel.field}>
              <label htmlFor={fieldId} className={panel.fieldLabel}>
                Rule
              </label>
              <textarea
                id={fieldId}
                className={styles.ruleInput}
                data-autofocus
                rows={5}
                value={content}
                aria-invalid={tooLong || Boolean(error)}
                aria-describedby={`${fieldId}-hint${error ? ` ${fieldId}-error` : ''}`}
                onChange={(event) => setContent(event.currentTarget.value)}
              />
              <small id={`${fieldId}-hint`} className={styles.fieldFoot}>
                <span>
                  Write it as guidance for Ava, for example: “When a caller asks
                  about parking, say it’s free behind the building.”
                </span>
                <span data-over={tooLong || undefined}>
                  {trimmed.length}/{MAX_RULE_LENGTH}
                </span>
              </small>
              {error ? (
                <small
                  id={`${fieldId}-error`}
                  className={panel.fieldError}
                  role="alert"
                >
                  <AlertTriangle size={14} aria-hidden="true" />
                  {error}
                </small>
              ) : null}
            </div>
            <p className={styles.lifecycleNote}>
              <Info size={15} aria-hidden="true" />
              {lifecycleNote(rule)}
            </p>
          </div>
        )}

        <footer className={panel.dialogFooter}>
          {mode === 'delete' && rule ? (
            <div className={panel.footerActions}>
              <Button
                ref={safeChoice}
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={() => setMode('edit')}
              >
                Keep rule
              </Button>
              <Button
                type="button"
                className={panel.dangerSolid}
                aria-disabled={saving || undefined}
                onClick={() => {
                  if (!saving) onDelete?.()
                }}
              >
                {saving ? (
                  <>
                    <Loader2
                      size={15}
                      className={panel.spin}
                      aria-hidden="true"
                    />{' '}
                    Deleting…
                  </>
                ) : (
                  'Delete rule'
                )}
              </Button>
            </div>
          ) : mode === 'discard' ? (
            <>
              <p className={panel.dialogNote} role="alert">
                Discard your changes to this rule?
              </p>
              <div className={panel.footerActions}>
                <Button
                  ref={safeChoice}
                  type="button"
                  variant="secondary"
                  onClick={() => setMode('edit')}
                >
                  Keep editing
                </Button>
                <Button type="button" onClick={onClose}>
                  Discard changes
                </Button>
              </div>
            </>
          ) : (
            <>
              {rule && onDelete ? (
                <Button
                  type="button"
                  variant="secondary"
                  className={`${panel.dangerOutline} ${styles.deleteRule}`}
                  disabled={saving}
                  onClick={() => ask('delete')}
                >
                  <Trash2 size={15} aria-hidden="true" /> Delete rule
                </Button>
              ) : (
                <span />
              )}
              <div className={panel.footerActions}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={saving}
                  onClick={requestClose}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={
                    saving || !trimmed || tooLong || (rule ? !changed : false)
                  }
                  onClick={() => onSubmit(trimmed)}
                >
                  {saving ? 'Saving…' : rule ? 'Save rule' : 'Add rule'}
                </Button>
              </div>
            </>
          )}
        </footer>
      </section>
    </div>,
    document.body
  )
}
